import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const useRealTimeUpdates = (onNewFinding, onFindingUpdate, onNewDefect, onDataRefresh, onWorkOrderUpdate, onWorkOrderCreated, organizationId = null, userId = null) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const handlersRef = useRef({ onNewFinding, onFindingUpdate, onNewDefect, onDataRefresh, onWorkOrderUpdate, onWorkOrderCreated });

  // Update handlers ref when they change
  useEffect(() => {
    handlersRef.current = { onNewFinding, onFindingUpdate, onNewDefect, onDataRefresh, onWorkOrderUpdate, onWorkOrderCreated };
  }, [onNewFinding, onFindingUpdate, onNewDefect, onDataRefresh, onWorkOrderUpdate, onWorkOrderCreated]);

  useEffect(() => {
    // Initialize socket connection
    const BACKEND_URL = process.env.REACT_APP_API_URL?.replace('/api', '');
    
    console.log('🔌 Connecting to WebSocket server:', BACKEND_URL);
    
    socketRef.current = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      randomizationFactor: 0.5,
      forceNew: false,
      autoConnect: true
    });

    const socket = socketRef.current;

    // Connection event handlers
    socket.on('connect', () => {
      console.log('✅ Connected to real-time updates');
      setIsConnected(true);
      setConnectionError(null);
      
      // Join organization-specific room
      if (organizationId && userId) {
        console.log(`🏢 Joining organization room for org: ${organizationId}, user: ${userId}`);
        socket.emit('join-organization', { organizationId, userId });
      } else {
        console.warn('⚠️ No organization or user ID provided - cannot join organization-specific room');
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ Disconnected from real-time updates:', reason);
      setIsConnected(false);
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, reconnect manually
        socket.connect();
      }
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      setConnectionError(null);
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Reconnection attempt', attemptNumber);
    });

    socket.on('reconnect_error', (error) => {
      console.error('❌ Reconnection error:', error.message);
      setConnectionError(error.message);
    });

    socket.on('reconnect_failed', () => {
      console.error('❌ Reconnection failed after all attempts');
      setConnectionError('Reconnection failed');
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Connection error:', error);
      setConnectionError(error.message);
      setIsConnected(false);
    });

    socket.on('connected', (data) => {
      console.log('🔔 Server confirmation:', data.message);
      console.log('📊 Connected clients:', data.clientsConnected);
    });

    socket.on('pong', () => {
      console.log('🏓 Pong received from server');
    });

    // Real-time event handlers
    socket.on('new-finding', (notification) => {
      console.log('🔔 New finding notification received:', notification);
      if (handlersRef.current.onNewFinding) {
        handlersRef.current.onNewFinding(notification);
      }
    });

    socket.on('finding-updated', (notification) => {
      console.log('🔔 Finding update notification received:', notification);
      if (handlersRef.current.onFindingUpdate) {
        handlersRef.current.onFindingUpdate(notification);
      }
    });

    socket.on('new-defect', (notification) => {
      console.log('🔔 New defect notification received:', notification);
      if (handlersRef.current.onNewDefect) {
        handlersRef.current.onNewDefect(notification);
      }
    });

    socket.on('data-refresh', (notification) => {
      console.log('🔄 Data refresh notification received:', notification);
      if (handlersRef.current.onDataRefresh) {
        handlersRef.current.onDataRefresh(notification);
      }
    });

    // Work order event handlers
    socket.on('workOrderStatusUpdate', (notification) => {
      console.log('🔔 Work order status update notification received:', notification);
      if (handlersRef.current.onWorkOrderUpdate) {
        handlersRef.current.onWorkOrderUpdate(notification);
      }
    });

    socket.on('work-order-created', (notification) => {
      console.log('🔔 Work order created notification received:', notification);
      if (handlersRef.current.onWorkOrderCreated) {
        handlersRef.current.onWorkOrderCreated(notification);
      }
    });

    socket.on('work-order-completed', (notification) => {
      console.log('🔔 Work order completed notification received:', notification);
      if (handlersRef.current.onWorkOrderUpdate) {
        handlersRef.current.onWorkOrderUpdate({
          ...notification,
          type: 'work_order_completed'
        });
      }
    });

    // Send periodic ping to keep connection alive
    const pingInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit('ping');
      }
    }, 30000); // Ping every 30 seconds

    // Cleanup on unmount
    return () => {
      console.log('🔌 Cleaning up WebSocket connection');
      clearInterval(pingInterval);
      if (socket) {
        socket.disconnect();
      }
    };
  }, []); // Empty dependency array - only run once

  // Function to manually reconnect
  const reconnect = () => {
    if (socketRef.current) {
      socketRef.current.connect();
    }
  };

  return {
    isConnected,
    connectionError,
    reconnect
  };
};

export default useRealTimeUpdates; 